import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import type { LoginResponse, MeResponse } from '@/types'
import { UNAUTHORIZED_EVENT, api } from '@/lib/api'
import {
  clearSession,
  getStoredVendor,
  getToken,
  getTokenRole,
  isTokenExpired,
  setStoredVendor,
  setToken,
} from '@/lib/session'

interface VendorAuthValue {
  vendor: MeResponse | null
  loading: boolean
  /** Sesión de cuenta (rename de "onboarding"): Koru validó al usuario, pero no hay una
   *  tienda elegida todavía (0, varias, o alguna sin reclamar). */
  isAccountSession: boolean
  login: (
    email: string,
    password: string,
    tenantHint?: string
  ) => Promise<{ firstLogin: boolean; isAdmin: boolean; account: boolean }>
  logout: () => void
  changePassword: (currentPassword: string, newPassword: string) => Promise<void>
  refresh: () => Promise<void>
}

const Ctx = createContext<VendorAuthValue | null>(null)

export function VendorAuthProvider({ children }: { children: React.ReactNode }) {
  const [vendor, setVendor] = useState<MeResponse | null>(() => {
    const token = getToken()
    return token && !isTokenExpired(token) ? getStoredVendor() : null
  })
  const [loading, setLoading] = useState(true)
  const [isAccountSession, setIsAccountSession] = useState(() => getTokenRole(getToken()) === 'account')

  const logout = useCallback(() => {
    clearSession()
    setVendor(null)
    setIsAccountSession(false)
  }, [])

  const refresh = useCallback(async () => {
    try {
      const me = await api.get<MeResponse>('/auth/me')
      setStoredVendor(me)
      setVendor(me)
    } catch {
      // 401 → el listener global desloguea. Otros errores: mantener estado local.
    }
  }, [])

  // Validación inicial de la sesión persistida.
  useEffect(() => {
    const token = getToken()
    if (!token || isTokenExpired(token)) {
      logout()
      setLoading(false)
      return
    }
    if (getTokenRole(token) === 'account') {
      setIsAccountSession(true)
      setLoading(false)
      return
    }
    void refresh().finally(() => setLoading(false))
  }, [logout, refresh])

  // Sesión invalidada por el edge (401) en cualquier request.
  useEffect(() => {
    const handler = () => logout()
    window.addEventListener(UNAUTHORIZED_EVENT, handler)
    return () => window.removeEventListener(UNAUTHORIZED_EVENT, handler)
  }, [logout])

  const login = useCallback(
    async (email: string, password: string, tenantHint?: string) => {
      const res = await api.post<LoginResponse>('/auth/login', { email, password, tenantHint })
      setToken(res.token)
      if (res.account) {
        // Sesión de cuenta: todavía no hay tenant, `/auth/me` (que exige rol vendor/admin)
        // no sirve acá — `requireVendor` la rechazaría con 401 y el listener global
        // desloguearía a mitad del selector. Se guarda el `vendor` del login tal cual.
        setStoredVendor(res.vendor)
        setVendor(res.vendor)
        setIsAccountSession(true)
      } else {
        // No confiar en `LoginResponse.vendor` (es un `SessionVendor`, sin `tenantStatus`):
        // pedirlo a `/auth/me` es lo que le da al contexto el estado real del tenant recién
        // después de un claim/login (A1). Sin esto, `AdminGate` no redirige a la conexión.
        setIsAccountSession(false)
        await refresh()
      }
      return { firstLogin: res.firstLogin, isAdmin: res.vendor.isAdmin, account: !!res.account }
    },
    [refresh]
  )

  const changePassword = useCallback(
    async (currentPassword: string, newPassword: string) => {
      await api.post('/auth/change-password', { currentPassword, newPassword })
      setVendor((v) => {
        if (!v) return v
        const next = { ...v, isFirstLogin: false }
        setStoredVendor(next)
        return next
      })
    },
    []
  )

  const value = useMemo<VendorAuthValue>(
    () => ({ vendor, loading, isAccountSession, login, logout, changePassword, refresh }),
    [vendor, loading, isAccountSession, login, logout, changePassword, refresh]
  )

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>
}

export function useVendorAuth(): VendorAuthValue {
  const ctx = useContext(Ctx)
  if (!ctx) throw new Error('useVendorAuth must be used within VendorAuthProvider')
  return ctx
}
